import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import { graphql, useStaticQuery } from 'gatsby';

import {
  Donation,
  SponsorshipDonation,
  StockDonation,
} from '../../generated/operation-ids';
import { SiteSettingsQuery } from '../../generated/types/gatsby';
import { languageFallback } from '../utils/localizations';
import { currentTheme } from '../utils/theme';

type ContentPage = {
  path: string;
  title: string;
};

export const useSiteSettings = (
  pageContext: SilverbackPageContext,
  donationPurpose?: (Donation & StockDonation & SponsorshipDonation) | {},
): {
  countryProgramme: {
    path: string;
    label: string;
  };
  contentHubPagePath?: string;
  headerCtaPath?: string;
  locationFamilyHeading?: string;
  loginRegisterPage?: ContentPage;
  locationSearchPage?: ContentPage;
  myProfilePage?: ContentPage;
  siteSearchPage?: ContentPage;
  geoBlockingLandingPage?: ContentPage;
  newsletterFormEmbedCode: string;
} => {
  const locale = languageFallback(pageContext.locale || 'de');
  const result = useStaticQuery<SiteSettingsQuery>(graphql`
    query SiteSettings {
      allDrupalSettings {
        nodes {
          contentHubPagePath
          contentHubPagePathCB
          contentHubPagePathCC
          contentHubPagePathYC
          headerCTAPathCB
          headerCTAPathCC
          headerCTAPathCS
          headerCTAPathYC
          countryProgramme {
            label
            langcode
            path
          }
          locationFamilyHeading
          loginRegisterPage {
            path
            title
          }
          myProfilePage {
            path
            title
          }
          locationSearchPage {
            title
            path
          }
          siteSearchPage {
            title
            path
          }
          siteSearchPageCB {
            title
            path
          }
          siteSearchPageCC {
            title
            path
          }
          siteSearchPageYC {
            title
            path
          }
          geoBlockingLandingPage {
            title
            path
          }
          newsletterFormEmbedCodeCS
          newsletterFormEmbedCodeCB
          newsletterFormEmbedCodeYC
        }
      }
    }
  `).allDrupalSettings.nodes.find(
    (item) => item.countryProgramme?.langcode === locale,
  )!;

  const ctaPath =
    currentTheme === 'caritas'
      ? result.headerCTAPathCS
      : currentTheme === 'youngcaritas'
      ? result.headerCTAPathYC
      : currentTheme === 'bergeinsatz'
      ? result.headerCTAPathCB
      : result.headerCTAPathCC;

  const siteSearchPage =
    currentTheme === 'caritas'
      ? result.siteSearchPage
      : currentTheme === 'youngcaritas'
      ? result.siteSearchPageYC
      : currentTheme === 'bergeinsatz'
      ? result.siteSearchPageCB
      : currentTheme === 'caritascare'
      ? result.siteSearchPageCC
      : undefined;

  const newsletterFormEmbedCode =
    currentTheme === 'caritas'
      ? result.newsletterFormEmbedCodeCS
      : currentTheme === 'youngcaritas'
      ? result.newsletterFormEmbedCodeYC
      : currentTheme === 'bergeinsatz'
      ? result.newsletterFormEmbedCodeCB
      : currentTheme === 'caritascare'
      ? undefined
      : undefined;

  // @ts-ignore
  const donationPurposePath = donationPurpose?.path;

  return {
    contentHubPagePath:
      currentTheme === 'caritas'
        ? result.contentHubPagePath
        : currentTheme === 'youngcaritas'
        ? result.contentHubPagePathYC
        : currentTheme === 'bergeinsatz'
        ? result.contentHubPagePathCB
        : result.contentHubPagePathCC,
    headerCtaPath:
      donationPurpose &&
      donationPurposePath &&
      // @ts-ignore
      donationPurpose?.__typename !== 'DrupalSponsorshipDonation' &&
      currentTheme === 'caritas'
        ? donationPurposePath
        : ctaPath,
    countryProgramme: {
      path: result?.countryProgramme?.path || '',
      label: result?.countryProgramme?.label || '',
    },
    locationFamilyHeading: result.locationFamilyHeading,
    locationSearchPage: result.locationSearchPage
      ? {
          path: result.locationSearchPage.path,
          title: result.locationSearchPage.title,
        }
      : undefined,
    loginRegisterPage: result.loginRegisterPage
      ? {
          path: result.loginRegisterPage.path,
          title: result.loginRegisterPage.title,
        }
      : undefined,
    myProfilePage: result.myProfilePage
      ? {
          path: result.myProfilePage.path,
          title: result.myProfilePage.title,
        }
      : undefined,
    siteSearchPage: siteSearchPage
      ? {
          path: siteSearchPage.path,
          title: siteSearchPage.title,
        }
      : undefined,
    geoBlockingLandingPage: result.geoBlockingLandingPage
      ? {
          path: result.geoBlockingLandingPage.path,
          title: result.geoBlockingLandingPage.title,
        }
      : undefined,
    newsletterFormEmbedCode: newsletterFormEmbedCode || '',
  };
};
